/**
 * useStoreSelector Hook
 * 
 * Library-agnostic selector hook that replaces useAppSelector.
 * Uses the store from context to select state.
 */

import { useSyncExternalStore, useMemo, useRef } from 'react';
import { useStoreContext } from '../context/StoreContext';
import type { Selector } from '../types';

/**
 * Default equality function (shallow comparison)
 */
function defaultEqualityFn<T>(a: T, b: T): boolean {
  return a === b;
}

/**
 * Hook to select state from the store
 * 
 * @example
 * ```typescript
 * const count = useStoreSelector(state => state.counter.count);
 * ```
 */
export function useStoreSelector<State = unknown, Selected = unknown>(
  selector: Selector<State, Selected>,
  equalityFn: (left: Selected, right: Selected) => boolean = defaultEqualityFn,
): Selected {
  const { store } = useStoreContext<State>();

  // Track previous selected value for equality check
  const prevSelectedRef = useRef<Selected>();
  const selectorRef = useRef(selector);
  const equalityFnRef = useRef(equalityFn);

  // Update refs when they change
  selectorRef.current = selector;
  equalityFnRef.current = equalityFn;

  // Use useSyncExternalStore for React 18+ compatibility
  const subscribe = useMemo(
    () => (callback: () => void) => store.subscribe(callback),
    [store],
  );

  const getSnapshot = useMemo(() => {
    return () => {
      const selected = selectorRef.current(store.getState());
      
      // Check equality if we have a previous value
      if (prevSelectedRef.current !== undefined) {
        if (equalityFnRef.current(prevSelectedRef.current, selected)) {
          return prevSelectedRef.current;
        }
      }
      
      prevSelectedRef.current = selected;
      return selected;
    };
  }, [store]);

  const getServerSnapshot = useMemo(() => {
    return () => selectorRef.current(store.getState());
  }, [store]);

  const selected = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  return selected;
}
